import type { SavedPlaybookMoment } from '@/types/learner';

/**
 * Compose the coach's "Play all my wins" walkthrough — ~90 seconds of stepping
 * through the saved plays in order, connecting each one to the skill it
 * demonstrates, then closing with the bring-to-the-real-room push.
 *
 * Pure function. Called from PlaybookScreen and passed to `playResponse()`
 * so the coach speaks it; the dialog flow on the CodeBaby side must NOT also
 * fire a "playbook summary" on mount (single-source rule).
 *
 * Target length ≈ 220–280 words (~90–110 seconds at ~150 wpm). Caps detailed
 * walk-throughs at 3 moments so the narrative stays focused — remaining
 * plays get a count-level acknowledgment rather than a full read.
 */

const MAX_DETAILED = 3;

// Phrasing here is persona-agnostic — the playbook aggregates moments from
// Linda, Kayla, and Frank sessions, so we don't lock pronouns to one
// persona. "The buyer" reads as the role they all share.
const TAG_PHRASE: Record<string, string> = {
  discovery: 'a discovery question that actually discovered something',
  pain_quantification: 'pain that you made the buyer put a number on',
  opening_hook: "an opening that didn't apologize for being there",
  small_yes_closes: 'a small-yes close — the exact shape we teach',
  commitment: 'a commitment ask at the right altitude',
  objection_reframe: 'an objection reframed into a discovery opening',
  proof_to_pain: 'proof tied back to the pain the buyer named',
  composure: 'composure when the room got uncomfortable',
};

function phraseForTags(tags: string[]): string {
  for (const t of tags) {
    if (TAG_PHRASE[t]) return TAG_PHRASE[t];
  }
  return 'a play worth keeping';
}

export function composePlaybookNarrative(moments: SavedPlaybookMoment[]): string {
  if (moments.length === 0) {
    return "You don't have any plays saved yet. Run a session, save your first win, and I'll walk you through it here.";
  }

  const parts: string[] = [];
  const detailed = moments.slice(0, MAX_DETAILED);
  const remainder = moments.length - detailed.length;

  parts.push(
    `Alright — let's walk through your plays. ${moments.length === 1 ? 'One saved moment' : `${moments.length} saved moments`} in here, and the reason they're worth keeping isn't that they worked once. It's that the shape of what you did is repeatable.`,
  );

  detailed.forEach((m, idx) => {
    const lead = idx === 0 ? 'Start with this one.' : idx === 1 ? 'Next play.' : 'And this one.';
    parts.push(`${lead} You said: "${m.quote}" That's ${phraseForTags(m.tags)}. ${m.coach_note}`);
  });

  if (remainder > 0) {
    parts.push(
      `You've got ${remainder} more ${remainder === 1 ? 'play' : 'plays'} in here — same pattern, same discipline. Read them yourself before your next session.`,
    );
  }

  parts.push(
    'These are your strongest moves on paper. The work is making them your default when the room is live and nobody is grading you. Run them back.',
  );

  return parts.join(' ');
}
